import i18n from './i18n';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getFlightSearch, SearchDate } from 'services/flight';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import { FlightSearch, GdsFlyAir, Gross2, Outbound } from 'ts/flight/interfaces/FlightSearch';
import { FlightDetails, Outbound as OutboundDetails } from 'ts/flight/interfaces/FlightDetailsResponse';
import { getCurrency } from 'utils/utils';
import { PassengerType, RoundTripMode } from 'constants/flight';
import { FlightTopBarStates } from 'ts/flight/classes/FlightTopBarStates';
import { filterMulti, filterOutbound } from './flight-filter';
import { BookingAges } from 'ts/flight/interfaces/FlightBookResponse';
import { Select } from 'ts/interfaces/Generic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactCountryFlag from 'react-country-flag';
import { animate } from 'animejs';

export const getFormattedCities = async (
  value: string,
  controller: AbortController
): Promise<Select[]> => {
  const responseOptions = await getFlightSearch(value, controller);
  const newFromOptions =
    responseOptions?.map((option) => {
      const label = option.regionNames ? option.regionNames.displayName : option.descWWW;
      let html;
      if (option.isChild === 'true' || option.airCityLang.extCode_1 === 'True') {
        html = (
          <div className="display-flex">
            <FontAwesomeIcon className="select-custom__chevron" icon={faChevronRight as any} />
            {label}
          </div>
        );
      } else {
        html = (
          <div className="display-flex">
            <ReactCountryFlag countryCode={option.countryCode} svg className="mr-5" /> {label}
          </div>
        );
      }

      return {
        value: option.code,
        label,
        html,
        data: option.internalOrder,
      };
    }) || [];
  return newFromOptions;
};

export const initFlights = (flights: FlightSearch[]) => {
  return flights.map((flight) => {
    return {
      ...flight,
      outbound: getBound(flight.outbound),
      inbound: getBound(flight.inbound),
      flightBounds: flight.flightBounds?.map((bound) => getBound(bound)),
      visible: true,
    };
  });
};

export const formatPriceFlight = (price: Gross2, passengersCount?: number) => {
  const currencySign = getCurrency(price.currency);
  const value = Math.round(price[price.currency.toLowerCase() as keyof Gross2] as number);

  if (passengersCount && passengersCount > 1) {
    return `${Math.round(value / passengersCount).toLocaleString()} ${currencySign}`;
  }

  return `${value.toLocaleString()}${currencySign} `;
};

export const getSortedFlights = (
  flights: FlightSearch[] | undefined,
  sort: string
): FlightSearch[] => {
  if (flights?.length) {
    switch (sort) {
      case 'priceIncrease':
        return flights.sort((a, b) => a.priceGrossAndTax - b.priceGrossAndTax);
      case 'priceDecrease':
        return flights.sort((a, b) => b.priceGrossAndTax - a.priceGrossAndTax);
      case 'airlineIncrease':
        return flights.sort((a, b) => a.airlineCode.localeCompare(b.airlineCode));
      case 'airlineDecrease':
        return flights.sort((a, b) => b.airlineCode.localeCompare(a.airlineCode));
      case 'durationIncrease':
        return flights.sort((a, b) => a.outbound.maxConnDTime - b.outbound.maxConnDTime);
      case 'durationDecrease':
        return flights.sort((a, b) => b.outbound.maxConnDTime - a.outbound.maxConnDTime);
      case 'departureOutboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.outbound.departureDateTime).getTime() -
            new Date(b.outbound.departureDateTime).getTime()
        );
      case 'departureOutboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.outbound.departureDateTime).getTime() -
            new Date(a.outbound.departureDateTime).getTime()
        );
      case 'returningOutboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.outbound.arrivalDateTime).getTime() -
            new Date(b.outbound.arrivalDateTime).getTime()
        );
      case 'returningOutboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.outbound.arrivalDateTime).getTime() -
            new Date(a.outbound.arrivalDateTime).getTime()
        );
      case 'departureInboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.inbound?.departureDateTime).getTime() -
            new Date(b.inbound?.departureDateTime).getTime()
        );
      case 'departureInboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.inbound?.departureDateTime).getTime() -
            new Date(a.inbound?.departureDateTime).getTime()
        );
      case 'returningInboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.inbound?.arrivalDateTime).getTime() -
            new Date(b.inbound?.arrivalDateTime).getTime()
        );
      case 'returningInboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.inbound?.arrivalDateTime).getTime() -
            new Date(a.inbound?.arrivalDateTime).getTime()
        );

      default:
        return flights;
    }
  }

  return [];
};

export const getFilteredFlights = (flights: FlightSearch[], filter: FlightFilterModel) => {
  if (isMultiTicket(flights)) {
    return filterMulti(flights, filter);
  } else {
    return filterOutbound(flights, filter);
  }
};

const getBound = (bound: Outbound) => {
  if (bound) {
    let gdsFlyAirs: GdsFlyAir[] = [...bound.gdsFlyAirs.map((gds) => ({ ...gds }))];
    return {
      ...bound,
      gdsFlyAirs,
    };
  } else {
    return bound;
  }
};

export const getBaggageTooltipContent = (
  noBaggage: boolean,
  outbond: Outbound | OutboundDetails
) => {
  if (!noBaggage && outbond.listOfFlyBaggagePrice?.length) {
    if (outbond.listOfFlyBaggagePrice[0].pieces === 1) {
      return i18n.t('flight.airline_allow_one');
    } else if (outbond.listOfFlyBaggagePrice[0].pieces > 1) {
      return i18n.t('flight.airline_allow_multiple', {
        count: outbond.listOfFlyBaggagePrice[0].pieces,
      });
    } else if (outbond.listOfFlyBaggagePrice[0].maxTotalWeight > 0) {
      const val =
        outbond.listOfFlyBaggagePrice[0].maxTotalWeight +
        outbond.listOfFlyBaggagePrice[0].maxTotalWeightUnit;
      return i18n.t('flight.airline_allow_max', { weight: val });
    }
  }
  return '';
};

export const getNoBaggage = (outbond: Outbound | OutboundDetails) => {
  if (!outbond.listOfFlyBaggagePrice?.length) {
    return true;
  }
  return (
    outbond.listOfFlyBaggagePrice[0].maxTotalWeight === 0 &&
    outbond.listOfFlyBaggagePrice[0].pieces === 0
  );
};

export const getActiveTopBarState = (mode: RoundTripMode, modes: FlightTopBarStates) => {
  switch (mode) {
    case RoundTripMode.Default:
      return modes.default;
    case RoundTripMode.Outbound:
      return modes.outbound;
    default:
      return modes.inbound;
  }
};

export const getBounds = (flight: FlightSearch | FlightDetails) => {
  if (flight.outbound && flight.inbound) {
    return [flight.outbound, { ...flight.inbound, flightType: 'return' }];
  } else if (flight.flightBounds?.length) {
    return flight.flightBounds;
  } else {
    return [flight.outbound];
  }
};

export const isMultiTicket = (flights: FlightSearch[]) => {
  return flights.length && flights[0].flightBounds?.length > 1;
};

export const getStopsText = (t: any, bound: OutboundDetails) => {
  if (bound.gdsFlyAirs.length === 1) {
    return t('filter.direct_flight');
  } else if (bound.gdsFlyAirs.length === 2) {
    return t('filter.one_stop');
  } else {
    return `${bound.gdsFlyAirs.length - 1} ${t('filter.stops_sm')}`;
  }
};

export const getPassengerStr = (t: any, type: string) => {
  switch (type) {
    case PassengerType.Adult:
      return t('search.adult');
    case PassengerType.Child:
      return t('search.child');
    case PassengerType.Infant:
      return t('search.infant');
    case PassengerType.Senior:
      return t('search.senior');
    case PassengerType.Youth:
      return t('search.youth');
    default:
      return '';
  }
};

export const getBookingAgesStr = (paxType: string, ages?: BookingAges) => {
  if (!ages) return '';
  switch (paxType as unknown as PassengerType) {
    case PassengerType.Adult:
      return '';
    case PassengerType.Child:
      return ages.CNN.join(' - ');
    case PassengerType.Infant:
      return ages.INF.join(' - ');
    case PassengerType.Senior:
      return ages.SRC.join(' - ');
    case PassengerType.Youth:
      return ages.YTH.join(' - ');
    default:
      return '';
  }
};

export const getBookingAges = (paxType: string, ages?: BookingAges) => {
  let result = ['0', '120'];
  if (!ages)
    return {
      min: +result[0],
      max: +result[1],
    };
  switch (paxType as unknown as PassengerType) {
    case PassengerType.Adult:
      result = ages.ADT;
      break;
    case PassengerType.Child:
      result = ages.CNN;
      break;
    case PassengerType.Infant:
      result = ages.INF;
      break;
    case PassengerType.Senior:
      result = ages.SRC;
      break;
    case PassengerType.Youth:
      result = ages.YTH;
      break;
    default:
      break;
  }

  return {
    min: +result[0],
    max: +result[1],
  };
};

export const getElapsedStr = (t: any, minutes?: number, hoursonly?: boolean) => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hoursonly) {
    return `${hours}${t('hours_sm')}`;
  }
  return hours === 0
    ? `${mins}${t('minutes_sm')}`
    : `${hours}${t('hours_sm')} ${mins}${t('minutes_sm')}`;
};

export const transformFlightData = (flight: FlightSearch) => {
  const outboundFlight = flight.outbound.gdsFlyAirs[0];

  return {
    id: flight.id,
    airlineCode: flight.airlineCode,
    airlineDesc: flight.airlineDesc,
    priceGross: flight.priceGross,
    provider: flight.provider,
    outbound: {
      flightNumber: outboundFlight.flightNumber,
      departureDateTime: outboundFlight.departureDateTime,
      arrivalDateTime: outboundFlight.arrivalDateTime,
      departureAirport: outboundFlight.departureAirport,
      arrivalAirport: outboundFlight.arrivalAirport,
      durationTime: outboundFlight.durationTime,
      availSeatsNo: outboundFlight.availSeatsNo,
      cabin: outboundFlight.cabin,
    },
  };
};

export const animateBound = (ref: HTMLDivElement | null, secondEl: boolean) => {
  if (ref) {
    const rect = ref.getBoundingClientRect();
    const scaleCopy = ref.cloneNode(true) as HTMLDivElement;
    scaleCopy.style.position = 'fixed';
    scaleCopy.style.left = rect.left + 'px';
    scaleCopy.style.top = rect.top + 'px';
    scaleCopy.style.zIndex = '9999';
    scaleCopy.style.pointerEvents = 'none';
    scaleCopy.style.transformOrigin = 'center center';
    document.body.appendChild(scaleCopy);

    const target = document.querySelector('.bound');
    const targetRect = target?.getBoundingClientRect();

    if (targetRect) {
      const sourceCenterX = rect.left + rect.width / 2;
      const sourceCenterY = rect.top + rect.height / 2;

      const isRtl = document.body.classList.contains('rtl');

      const targetCenterX = isRtl
        ? targetRect.right - (secondEl ? 170 : 0) - targetRect.width / 2 - 140
        : targetRect.right + (secondEl ? 170 : 0) + targetRect.width / 2 + 140;
      const targetCenterY = targetRect.top + targetRect.height / 2;

      const tabletTargetX = isRtl
        ? window.innerWidth - 80 - (secondEl ? 120 : 0)
        : 80 + (secondEl ? 120 : 0);
      const tabletTargetY = targetCenterY + 50;
      const isTablet = window.innerWidth < 768;

      const translateX = (isTablet ? tabletTargetX : targetCenterX) - sourceCenterX;
      const translateY = (isTablet ? tabletTargetY : targetCenterY) - sourceCenterY;

      animate(scaleCopy, {
        translateX: [0, translateX],
        translateY: [0, translateY],
        scale: [1, 0.3],
        duration: 1000,
        easing: 'easeOutQuad',
        complete: () => {
          setTimeout(() => {
            scaleCopy.remove();
          }, 1000);
        },
      });
    } else {
      animate(scaleCopy, {
        scale: [1, 0.3],
        duration: 1000,
        easing: 'easeOutQuad',
        complete: () => {
          setTimeout(() => {
            scaleCopy.remove();
          }, 1000);
        },
      });
    }
  }
};


export const getDisabledDates = (dates?: SearchDate[]) => {
  if (!dates?.length) {
    return [];
  }
  const days = buildAllDates(dates[0].date, dates[dates.length - 1].date);
  return days.filter((day) => {
    return !dates.some((date) => {
      return (
        day.getDate() === date.date.getDate() &&
        day.getMonth() === date.date.getMonth() &&
        day.getFullYear() === date.date.getFullYear()
      );
    });
  });
};

const buildAllDates = (startDate: Date, endDate: Date) => {
  const dates = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};